import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { ProxyTypeCard } from "@/ui/browse/ProxyTypeCard";
import { Highlighter } from "@/ui/Highlighter";
import { Tick } from "@/components/newcustomize/ProxyDetails/ProxyTabs/ProxyTabs";
import { RouteMUILink } from "@/components/common/styles";
import useCustomizeQuery from "@/components/newcustomize/hooks/useCustomizeQuery";
import useFromPrice from "@/hooks/useFromPrice";
import { userFormatCurrency } from "@/utils/textUtils";
import { TProxyTabType } from "@/components/newcustomize/utils/makeProxyTabType";
import {
  PRICING_PLAN_CATALOG,
  PlanProduct,
  PlanTier,
} from "@/config/pricingPlanCatalog";
import { PROXY_PLAN_MAP } from "./proxyPlanMap";

/** Resolves a single tier's display price, per-unit price, and availability. */
function resolveTierPrice(
  tier: PlanTier,
  fetched: { price?: number; isOutOfStock: boolean }
) {
  const price = tier.staticPrice ?? fetched.price;
  return {
    price,
    pricePerUnit: price === undefined ? undefined : price / tier.unitCount,
    isOutOfStock: fetched.isOutOfStock,
  };
}

/**
 * Resolves the active product's plan tiers, with each tier's price pulled from
 * the pricing API (so it tracks the backend — including pricing experiments and
 * existing-customer pricing via useFromPrice). A tier whose proxy count exceeds
 * available stock is flagged `isOutOfStock`.
 */
const usePlanData = (planKey: TProxyTabType) => {
  const product: PlanProduct | undefined = PRICING_PLAN_CATALOG[planKey];

  // Each product has three tiers; resolve each tier's config up front, then
  // call the hook a fixed number of times (in order) so the rules of hooks
  // hold. Tiers with a `staticPrice` (Verified) resolve to `null` (no request).
  const configs = [0, 1, 2].map(
    (i) => product?.tiers[i]?.pricingConfig ?? null
  );
  const tier0 = useFromPrice(configs[0]);
  const tier1 = useFromPrice(configs[1]);
  const tier2 = useFromPrice(configs[2]);

  if (!product) return undefined;

  const resolved = [tier0, tier1, tier2];
  const plans = product.tiers.map((tier, index) => ({
    title: tier.title,
    unit: tier.unit,
    params: tier.params,
    isDefault: tier.isDefault,
    ...resolveTierPrice(tier, resolved[index]),
  }));

  return { ...product, plans };
};

type ResolvedPlanData = NonNullable<ReturnType<typeof usePlanData>>;

// Prefer the "most popular" tier, but never default to an out-of-stock one —
// fall back to the first in-stock tier (or 0 if everything is unavailable).
function pickDefaultTierIndex(plan: ResolvedPlanData | undefined) {
  if (!plan) return 0;
  const isAvailable = (p: { isOutOfStock?: boolean }) => !p.isOutOfStock;
  const defaultIdx = plan.plans.findIndex((p) => p.isDefault && isAvailable(p));
  if (defaultIdx >= 0) return defaultIdx;
  const firstAvailable = plan.plans.findIndex(isAvailable);
  if (firstAvailable >= 0) return firstAvailable;
  return Math.max(
    plan.plans.findIndex((p) => p.isDefault),
    0
  );
}

/**
 * Tier selection state for a product's Popular Plans purchase flow, plus the
 * customize deep-link for buying the selected tier.
 */
export function usePlanTierSelection(planKey: TProxyTabType) {
  const plan = usePlanData(planKey);

  const defaultTierIndex = pickDefaultTierIndex(plan);
  const [selectedIndex, setSelectedIndex] = useState(defaultTierIndex);

  useEffect(() => {
    setSelectedIndex(defaultTierIndex);
  }, [defaultTierIndex]);

  const selectedPlan = plan?.plans[selectedIndex];

  const { link: buyNowHref, isLoading } = useCustomizeQuery({
    proxyPlan: PROXY_PLAN_MAP[planKey],
    skipDefaults: true,
    overrides: selectedPlan?.params,
  });

  return {
    plan,
    selectedIndex,
    setSelectedIndex,
    selectedPlan,
    buyNowHref,
    isLoading,
  };
}

/** The row of selectable tier cards of a product's Popular Plans flow. */
export function PlanTierRow({
  plans,
  selectedIndex,
  onSelect,
  sx,
  spacing = 3,
}: {
  plans: ResolvedPlanData["plans"];
  selectedIndex: number;
  onSelect: (index: number) => void;
  sx?: React.ComponentProps<typeof Stack>["sx"];
  spacing?: React.ComponentProps<typeof Stack>["spacing"];
}) {
  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={spacing} sx={sx}>
      {plans.map(
        (
          { title, price, pricePerUnit, unit, isDefault, isOutOfStock },
          index
        ) => (
          <PriceBox
            title={title}
            price={price}
            pricePerUnit={pricePerUnit}
            onClick={() => onSelect(index)}
            isActive={index === selectedIndex}
            key={index}
            unit={unit}
            isPopular={isDefault}
            isOutOfStock={isOutOfStock}
          />
        )
      )}
    </Stack>
  );
}

/** The "Need a Different plan?" escape hatch below the tier cards. */
export function CheckPricingLink() {
  return (
    <Typography variant="body2">
      Need a Different plan?{" "}
      <RouteMUILink sx={{ cursor: "pointer" }} to="/subscription/choose-plan">
        Check Pricing For All Plans
      </RouteMUILink>
    </Typography>
  );
}

const PriceBoxContent = ({
  price,
  pricePerUnit,
  unit,
  isOutOfStock,
}: {
  price: number | undefined;
  pricePerUnit: number | undefined;
  unit: string;
  isOutOfStock?: boolean;
}) => {
  if (isOutOfStock) {
    return (
      <Typography variant="h6" color="text.secondary">
        Out of stock
      </Typography>
    );
  }

  if (price === undefined || pricePerUnit === undefined) {
    return (
      <Box>
        <Skeleton variant="text" width={100} height={40} />
        <Skeleton variant="text" width={140} />
      </Box>
    );
  }

  // A value that changes beside fixed text gets an element of its own. Page
  // translation (Chrome, Google Translate) swaps text nodes for its own
  // copies; React rewrites an element whose only child is the value, but
  // updates a value set beside other text off screen, so switching plans
  // kept the old unit and total.
  return (
    <>
      <Stack direction="row" alignItems="baseline">
        <Typography variant="h4" fontWeight="bold" color="primary.dark">
          {userFormatCurrency(pricePerUnit)}
        </Typography>
        <Typography variant="caption">
          /<span>{unit}</span>
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Total: <span>{userFormatCurrency(price)}</span>/mo
      </Typography>
    </>
  );
};

// Out-of-stock tiers are greyed out and not selectable.
const cardStateSx = (interactive: boolean) =>
  interactive
    ? { cursor: "pointer", opacity: 1 }
    : { cursor: "not-allowed", opacity: 0.5 };

const PriceBoxHeader = ({
  title,
  isPopular,
  isOutOfStock,
}: {
  title: string;
  isPopular?: boolean;
  isOutOfStock?: boolean;
}) => (
  <Stack direction={"row"} spacing={2} alignItems={"center"}>
    <Typography variant="overline">{title}</Typography>
    {isPopular && !isOutOfStock ? (
      <Highlighter
        data-testid="popular"
        severity="secondary"
        innerVariant="opacity"
        size="small"
      >
        Most Popular
      </Highlighter>
    ) : null}
  </Stack>
);

const PriceBox = ({
  title,
  price,
  pricePerUnit,
  onClick,
  isActive,
  unit,
  isPopular,
  isOutOfStock,
}: {
  title: string;
  price: number | undefined;
  pricePerUnit: number | undefined;
  isActive?: boolean;
  onClick: () => void;
  unit: string;
  isPopular?: boolean;
  isOutOfStock?: boolean;
}) => {
  const showActive = !!isActive && !isOutOfStock;

  return (
    <ProxyTypeCard
      selected={showActive}
      sx={{
        p: 3,
        borderRadius: 2,
        flex: 1,
        position: "relative",
        ...cardStateSx(!isOutOfStock),
      }}
      onClick={isOutOfStock ? undefined : onClick}
    >
      {showActive ? <Tick /> : null}
      <Stack
        spacing={2}
        justifyContent={"space-between"}
        sx={{ height: "100%" }}
      >
        <PriceBoxHeader
          title={title}
          isPopular={isPopular}
          isOutOfStock={isOutOfStock}
        />

        <PriceBoxContent
          price={price}
          pricePerUnit={pricePerUnit}
          unit={unit}
          isOutOfStock={isOutOfStock}
        />
      </Stack>
    </ProxyTypeCard>
  );
};
